import { ReactNode } from "react";
import { FaArrowLeft } from "react-icons/fa";

interface OnboardLayoutProps {
  title: ReactNode;
  subtitle: ReactNode;
  illustration?: ReactNode;
  /** Дополнительный блок под подзаголовком, например список возможностей */
  features?: ReactNode;
  backLabel?: string;
  onBack?: () => void;
  children: ReactNode;
}

/**
 * Полноэкранный макет (без меню и шапки админки): слева тёмная панель с логотипом,
 * иллюстрацией, заголовком и «Назад», справа — содержимое.
 * Используется для подключения Instagram и страницы входа.
 * Без flex-col у корня: глобальный .flex-col в стилях админки перебивает lg:flex-row.
 */
export const OnboardLayout = ({
  title,
  subtitle,
  illustration,
  features,
  backLabel = "Назад",
  onBack,
  children,
}: OnboardLayoutProps) => (
  <div className="min-h-screen w-full flex flex-wrap content-start lg:content-stretch bg-white">
    <aside
      className="w-full lg:w-1/2 lg:min-h-screen flex flex-col px-6 py-6 sm:px-12 sm:py-10 lg:px-[10%] lg:py-14 text-white"
      style={{
        background:
          "radial-gradient(120% 90% at 30% 40%, #4a2366 0%, #351a4b 55%, #261236 100%)",
      }}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center overflow-hidden">
          <img
            src="/media/images/ubee-logo.png"
            alt="Ubee AI"
            className="w-9 h-9 object-contain"
          />
        </div>
        <span className="text-2xl font-bold tracking-tight">Ubee AI</span>
      </div>

      <div className="flex-grow flex flex-col justify-center py-8 lg:py-0">
        {illustration && <div className="mb-8 lg:mb-12">{illustration}</div>}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-3">
          {title}
        </h1>
        <p className="text-lg text-white/60">{subtitle}</p>
        {features && <div className="mt-8">{features}</div>}
      </div>

      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="hidden lg:flex items-center gap-3 text-lg text-white/90 hover:text-white w-fit transition-colors"
        >
          <FaArrowLeft size={18} />
          {backLabel}
        </button>
      )}
    </aside>

    <main className="w-full lg:w-1/2 flex items-center px-6 py-10 sm:px-12 lg:px-[8%]">
      <div className="w-full max-w-[480px]">
        {children}

        {/* Обёртка без flex: глобальный .flex перебивает lg:hidden */}
        {onBack && (
          <div className="lg:hidden mt-8 text-center">
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <FaArrowLeft size={14} />
              {backLabel}
            </button>
          </div>
        )}
      </div>
    </main>
  </div>
);

export default OnboardLayout;
